import React, { useEffect, useRef, useState } from 'react';
import { api, uploadWithFormData, downloadApiFile } from '../api';
import { useBankConfig } from '../bank-config';

const BATCHES_PAGE_SIZE = 10;

const STATUS_LABELS = {
    preview: 'Предпросмотр',
    queued: 'Очередь создана',
    sending: 'Отправляется',
    finished: 'Готово',
    finished_with_errors: 'Готово с ошибками',
    error: 'Ошибка',

    pending: 'Ожидает',
    processing: 'Отправляется',
    sent: 'Отправлено',

    contacts_lookup: 'Поиск в contacts',
    ready_file: 'Готовый файл'
};

function statusLabel(status) {
    return STATUS_LABELS[status] || status || '-';
}

function formatDate(value) {
    if (!value) return '-';

    const date = new Date(String(value).replace(' ', 'T'));

    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).format(date);
}

function FileDropzone({ file, onFile, label = 'Файл XLSX / XLS / CSV', disabled = false }) {
    const [dragActive, setDragActive] = useState(false);

    function handleDrop(event) {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(false);

        if (disabled) return;

        const nextFile = event.dataTransfer?.files?.[0];
        if (nextFile) onFile(nextFile);
    }

    function handleDrag(event) {
        event.preventDefault();
        event.stopPropagation();

        if (disabled) return;

        if (event.type === 'dragenter' || event.type === 'dragover') {
            setDragActive(true);
        }

        if (event.type === 'dragleave') {
            setDragActive(false);
        }
    }

    return (
        <label
            className={`dropzone${dragActive ? ' active' : ''}${file ? ' has-file' : ''}${disabled ? ' disabled' : ''}`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
        >
            <input
                key={file ? file.name : 'empty'}
                className="dropzone-input"
                type="file"
                accept=".xlsx,.xls,.csv"
                disabled={disabled}
                onChange={(event) => onFile(event.target.files?.[0] || null)}
            />
            <span className="dropzone-title">{file ? file.name : label}</span>
            <span className="dropzone-hint">
                {disabled
                    ? 'Сначала выберите этот режим'
                    : 'Перетащите файл сюда или нажмите для выбора файла'}
            </span>
        </label>
    );
}

export function LeadsPage() {
    const bankConfig = useBankConfig();
    const { isTochka } = bankConfig;
    const [mode, setMode] = useState('contacts_lookup');

    const [lookupForm, setLookupForm] = useState({
        organizationColumn: 1,
        hasHeader: true,
        advCode: '',
        productCode: 'LP_RKO',
        file: null
    });

    const [readyForm, setReadyForm] = useState({
        apiKey: '',
        apiUrl: 'https://partner.alfabank.ru/public-api/v2/leads',
        productCode: 'LP_RKO',
        hasHeader: true,
        file: null
    });

    const [batches, setBatches] = useState([]);
    const [batch, setBatch] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [queueing, setQueueing] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const pollingRef = useRef(null);

    async function loadBatches(nextPage = page, silent = false) {
        if (!silent) setLoading(true);

        try {
            const data = await api(`/leads/batches?page=${encodeURIComponent(nextPage)}&pageSize=${BATCHES_PAGE_SIZE}`);

            setBatches(Array.isArray(data?.items) ? data.items : []);
            setPage(Number(data?.page || nextPage || 1));
            setTotalPages(Number(data?.totalPages || 1));
        } finally {
            if (!silent) setLoading(false);
        }
    }

    async function loadBatch(batchId = batch?.id, silent = false) {
        if (!batchId) return;

        if (!silent) setLoading(true);

        try {
            const data = await api(`/leads/batches/${batchId}`);
            setBatch(data);
        } finally {
            if (!silent) setLoading(false);
        }
    }

    useEffect(() => {
        loadBatches(1).catch((err) => setError(err.message || 'Не удалось загрузить список'));
    }, []);

    useEffect(() => {
        if (pollingRef.current) clearInterval(pollingRef.current);

        pollingRef.current = setInterval(() => {
            if (document.hidden) return;

            loadBatches(page, true).catch(() => {});

            if (batch?.id) {
                loadBatch(batch.id, true).catch(() => {});
            }
        }, 3000);

        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, [page, batch?.id]);

    async function uploadContactsLookupFile() {
        if (!lookupForm.file) {
            setError('Выберите файл');
            return;
        }

        if (!isTochka && !lookupForm.advCode.trim()) {
            setError('Укажите advCode');
            return;
        }

        setUploading(true);
        setError('');
        setMessage('');

        try {
            const formData = new FormData();

            formData.append('organizationColumn', String(lookupForm.organizationColumn || 1));
            formData.append('hasHeader', lookupForm.hasHeader ? 'true' : 'false');
            formData.append('advCode', lookupForm.advCode);
            formData.append('productCode', lookupForm.productCode || 'LP_RKO');
            formData.append('file', lookupForm.file);

            const data = await uploadWithFormData('/leads/upload-contacts-lookup', formData, { timeoutMs: 600000 });

            setBatch(data);
            await loadBatches(1, true);

            setMessage('Файл обработан. Проверьте результат и сформируйте очередь отправки.');
        } catch (err) {
            setError(err.message || 'Не удалось обработать файл');
        } finally {
            setUploading(false);
        }
    }

    async function uploadReadyFile() {
        if (!readyForm.file) {
            setError('Выберите файл');
            return;
        }

        if (!isTochka && !readyForm.apiKey.trim()) {
            setError('Укажите API-key банка');
            return;
        }

        if (!isTochka && !readyForm.apiUrl.trim()) {
            setError('Укажите URL банка');
            return;
        }

        setUploading(true);
        setError('');
        setMessage('');

        try {
            const formData = new FormData();

            formData.append('apiKey', readyForm.apiKey);
            formData.append('apiUrl', readyForm.apiUrl);
            formData.append('productCode', readyForm.productCode || 'LP_RKO');
            formData.append('hasHeader', readyForm.hasHeader ? 'true' : 'false');
            formData.append('file', readyForm.file);

            const data = await uploadWithFormData('/leads/upload-ready-file', formData, { timeoutMs: 600000 });

            setBatch(data);
            await loadBatches(1, true);

            setMessage('Готовый файл загружен. Очередь создана сразу.');
        } catch (err) {
            setError(err.message || 'Не удалось загрузить готовый файл');
        } finally {
            setUploading(false);
        }
    }

    async function createQueue() {
        if (!batch?.id) return;

        setQueueing(true);
        setError('');
        setMessage('');

        try {
            const data = await api(`/leads/batches/${batch.id}/queue`, {
                method: 'POST',
                body: JSON.stringify({})
            });

            setBatch(data);
            await loadBatches(page, true);

            setMessage('Очередь создана. Заявки будут отправляться по одной с паузой 5–15 секунд.');
        } catch (err) {
            setError(err.message || 'Не удалось создать очередь');
        } finally {
            setQueueing(false);
        }
    }

    async function deleteBatch(batchId) {
        if (!batchId) return;

        if (!window.confirm('Удалить эту загрузку и связанную очередь?')) {
            return;
        }

        setActionLoadingId(`delete:${batchId}`);
        setError('');
        setMessage('');

        try {
            await api(`/leads/batches/${batchId}`, {
                method: 'DELETE'
            });

            if (batch?.id === batchId) {
                setBatch(null);
            }

            await loadBatches(page, true);
            setMessage('Загрузка удалена');
        } catch (err) {
            setError(err.message || 'Не удалось удалить загрузку');
        } finally {
            setActionLoadingId(null);
        }
    }

    async function retryBatchErrors(batchId) {
        if (!batchId) return;

        if (!window.confirm('Переотправить ошибочные заявки из этой загрузки? Уже отправленные заявки не будут затронуты.')) {
            return;
        }

        setActionLoadingId(`retry:${batchId}`);
        setError('');
        setMessage('');

        try {
            const data = await api(`/leads/batches/${batchId}/retry-errors`, {
                method: 'POST',
                body: JSON.stringify({})
            });

            if (batch?.id === batchId) {
                setBatch(data);
            }

            await loadBatches(page, true);
            setMessage('Ошибочные заявки снова поставлены в очередь');
        } catch (err) {
            setError(err.message || 'Не удалось переотправить ошибки');
        } finally {
            setActionLoadingId(null);
        }
    }

    async function downloadBatchExcel(batchId) {
        if (!batchId) return;

        setActionLoadingId(`excel:${batchId}`);
        setError('');
        setMessage('');

        try {
            await downloadApiFile(
                `/leads/batches/${batchId}/export`,
                `leads_batch_${batchId}.xlsx`,
                { timeoutMs: 120000 }
            );
        } catch (err) {
            setError(err.message || 'Не удалось скачать Excel');
        } finally {
            setActionLoadingId(null);
        }
    }

    
    const rows = Array.isArray(batch?.rows) ? batch.rows : [];
    const queue = Array.isArray(batch?.queue) ? batch.queue : [];
    const isContactsLookupBatch = !batch?.source_type || batch.source_type === 'contacts_lookup';

    return (
        <div className="page-stack">
            <section className="card">
                <div className="section-header">
                    <div>
                        <h2>Лиды</h2>
                        <p className="muted section-note">
                            Выберите режим загрузки. Активный режим подсвечен оранжевой рамкой и меткой «Активен».
                        </p>
                    </div>
                </div>

                {error ? <div className="error-banner card">{error}</div> : null}
                {bankConfig.error ? <div className="error-banner card">{bankConfig.error}</div> : null}
                {isTochka ? <div className="card muted">Точка: ключ и адрес API берутся из настроек банка. Коды продуктов, Adv Code и код города не требуются. {!bankConfig.liveMode ? 'Боевой режим выключен: очередь ожидает включения, заявки в банк не отправляются.' : ''}</div> : null}
                {message ? <div className="success-banner card">{message}</div> : null}

                <div className="lead-mode-grid">
                    <div
                        className={`lead-mode-card card${mode === 'contacts_lookup' ? ' active' : ''}`}
                    >
                        <div className="lead-mode-head">
                            <button
                                type="button"
                                className="lead-mode-title"
                                onClick={() => setMode('contacts_lookup')}
                                aria-pressed={mode === 'contacts_lookup'}
                            >
                                Режим 1: поиск в contacts
                            </button>
                            {mode === 'contacts_lookup' ? (
                                <span className="active-badge">Активен</span>
                            ) : (
                                <button type="button" className="secondary" onClick={() => setMode('contacts_lookup')}>
                                    Выбрать
                                </button>
                            )}
                        </div>

                        <div className="lead-mode-body">
                            <div className="lead-mode-form">
                                {!isTochka ? <><label>
                                    advCode
                                    <input
                                        disabled={mode !== 'contacts_lookup'}
                                        value={lookupForm.advCode}
                                        onChange={(event) => setLookupForm((prev) => ({ ...prev, advCode: event.target.value }))}
                                        placeholder="advCode"
                                    />
                                </label>

                                <label>
                                    productCode
                                    <input
                                        disabled={mode !== 'contacts_lookup'}
                                        value={lookupForm.productCode}
                                        onChange={(event) => setLookupForm((prev) => ({ ...prev, productCode: event.target.value }))}
                                        placeholder="LP_RKO"
                                    />
                                </label></> : null}

                                <label>
                                    Колонка с названием организации, с 1
                                    <input
                                        disabled={mode !== 'contacts_lookup'}
                                        type="number"
                                        min="1"
                                        value={lookupForm.organizationColumn}
                                        onChange={(event) => setLookupForm((prev) => ({ ...prev, organizationColumn: event.target.value }))}
                                    />
                                </label>

                                <label className="checkbox">
                                    <input
                                        disabled={mode !== 'contacts_lookup'}
                                        type="checkbox"
                                        checked={lookupForm.hasHeader}
                                        onChange={(event) => setLookupForm((prev) => ({ ...prev, hasHeader: event.target.checked }))}
                                    />
                                    В первой строке заголовки
                                </label>

                                <FileDropzone
                                    file={lookupForm.file}
                                    onFile={(file) => setLookupForm((prev) => ({ ...prev, file }))}
                                    label="Файл с названиями организаций"
                                    disabled={mode !== 'contacts_lookup'}
                                />

                                <button type="button" onClick={uploadContactsLookupFile} disabled={mode !== 'contacts_lookup' || uploading || bankConfig.loading || Boolean(bankConfig.error)}>
                                    {uploading && mode === 'contacts_lookup' ? 'Обрабатываем...' : 'Обработать файл'}
                                </button>
                            </div>

                            <div className="lead-mode-description">
                                <strong>Описание</strong>
                                <p className="muted">
                                    Берет названия организаций из файла, убирает дубли, ищет записи в contacts.
                                    API-key и URL банка берутся из настроек.
                                </p>
                                <p className="muted">
                                    Если города нет в контакте, система пытается определить его по первым двум цифрам ИНН через таблицу «Настройки / Города».
                                </p>
                            </div>
                        </div>
                    </div>

                    <div
                        className={`lead-mode-card card${mode === 'ready_file' ? ' active' : ''}`}
                    >
                        <div className="lead-mode-head">
                            <button
                                type="button"
                                className="lead-mode-title"
                                onClick={() => setMode('ready_file')}
                                aria-pressed={mode === 'ready_file'}
                            >
                                Режим 2: готовый файл
                            </button>
                            {mode === 'ready_file' ? (
                                <span className="active-badge">Активен</span>
                            ) : (
                                <button type="button" className="secondary" onClick={() => setMode('ready_file')}>
                                    Выбрать
                                </button>
                            )}
                        </div>

                        <div className="lead-mode-body">
                            <div className="lead-mode-form">
                                {!isTochka ? <><label>
                                    API-key банка
                                    <input
                                        disabled={mode !== 'ready_file'}
                                        type="password"
                                        value={readyForm.apiKey}
                                        onChange={(event) => setReadyForm((prev) => ({ ...prev, apiKey: event.target.value }))}
                                        placeholder="API-key"
                                    />
                                </label>

                                <label>
                                    URL банка
                                    <input
                                        disabled={mode !== 'ready_file'}
                                        value={readyForm.apiUrl}
                                        onChange={(event) => setReadyForm((prev) => ({ ...prev, apiUrl: event.target.value }))}
                                        placeholder="https://partner.alfabank.ru/public-api/v2/leads"
                                    />
                                </label>

                                <label>
                                    productCode
                                    <input
                                        disabled={mode !== 'ready_file'}
                                        value={readyForm.productCode}
                                        onChange={(event) => setReadyForm((prev) => ({ ...prev, productCode: event.target.value }))}
                                        placeholder="LP_RKO"
                                    />
                                </label></> : null}

                                <label className="checkbox">
                                    <input
                                        disabled={mode !== 'ready_file'}
                                        type="checkbox"
                                        checked={readyForm.hasHeader}
                                        onChange={(event) => setReadyForm((prev) => ({ ...prev, hasHeader: event.target.checked }))}
                                    />
                                    В первой строке заголовки
                                </label>

                                <FileDropzone
                                    file={readyForm.file}
                                    onFile={(file) => setReadyForm((prev) => ({ ...prev, file }))}
                                    label="Готовый файл для банка"
                                    disabled={mode !== 'ready_file'}
                                />

                                <button type="button" onClick={uploadReadyFile} disabled={mode !== 'ready_file' || uploading || bankConfig.loading || Boolean(bankConfig.error)}>
                                    {uploading && mode === 'ready_file' ? 'Загружаем...' : 'Загрузить в очередь'}
                                </button>
                            </div>

                            <div className="lead-mode-description">
                                <strong>Описание</strong>
                                <p>
                                    API_KEY ALFA1: <br/>f570dddd-ea9f-47b5-8730-6116f094c752 <br/><br/>
                                    API_KEY ALFA2: <br/>0a208a11-4940-48aa-8d99-34de258047d2
                                </p>
                                <p className="muted">
                                    Не ищет данные в contacts. Берет данные прямо из файла и сразу формирует очередь отправки.
                                </p>
                                <p className="muted">
                                    Формат колонок: Ogrn, Inn, Kpp, OrganizationName, Город, fullName, fullName, phoneNumber, {isTochka ? 'Комментарий' : 'advCode'}.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="card">
                <h3>Последние загрузки</h3>

                {loading && !batches.length ? <p>Загрузка...</p> : null}

                <div className="table-wrap">
                    <table className="table">
                        <thead>
                        <tr>
                            <th>ID</th>
                            <th>Режим</th>
                            <th>Файл</th>
                            <th>Статус</th>
                            <th>Загружено</th>
                            <th>Уникальных</th>
                            <th>Найдено</th>
                            <th>Готово</th>
                            <th>Очередь</th>
                            <th>Отправлено</th>
                            <th>Ошибки</th>
                            <th>Дата</th>
                            <th>Действия</th>
                        </tr>
                        </thead>

                        <tbody>
                        {batches.map((item) => (
                            <tr key={item.id} className={batch?.id === item.id ? 'selected-row' : ''}>
                                <td>{item.id}</td>
                                <td>{statusLabel(item.source_type)}</td>
                                <td>{item.original_name}</td>
                                <td>{statusLabel(item.status)}</td>
                                <td>{item.uploaded_total}</td>
                                <td>{item.unique_total}</td>
                                <td>{item.found_total}</td>
                                <td>{item.ready_total}</td>
                                <td>{item.queued_total}</td>
                                <td>{item.sent_total}</td>
                                <td>{item.error_total}</td>
                                <td>{formatDate(item.created_at)}</td>
                                <td>
                                    <div className="inline-actions">
                                        <button type="button" onClick={() => loadBatch(item.id)}>
                                            Открыть
                                        </button>

                                        {Number(item.error_total || 0) > 0 ? (
                                            <button
                                                type="button"
                                                onClick={() => retryBatchErrors(item.id)}
                                                disabled={actionLoadingId === `retry:${item.id}`}
                                            >
                                                {actionLoadingId === `retry:${item.id}` ? 'Ставим...' : 'Переотправить'}
                                            </button>
                                        ) : null}

                                        <button
                                            type="button"
                                            className="danger"
                                            onClick={() => deleteBatch(item.id)}
                                            disabled={actionLoadingId === `delete:${item.id}`}
                                        >
                                            {actionLoadingId === `delete:${item.id}` ? 'Удаляем...' : 'Удалить'}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}

                        {!batches.length ? (
                            <tr>
                                <td colSpan="13">Загрузок пока нет</td>
                            </tr>
                        ) : null}
                        </tbody>
                    </table>
                </div>

                <div className="inline-actions">
                    <button type="button" disabled={page <= 1} onClick={() => loadBatches(page - 1)}>
                        Назад
                    </button>

                    <span className="muted">Страница {page} из {totalPages}</span>

                    <button type="button" disabled={page >= totalPages} onClick={() => loadBatches(page + 1)}>
                        Вперед
                    </button>
                </div>
            </section>

            {batch ? (
                <section className="card">
                    <h3>Результаты файла #{batch.id}</h3>

                    <p className="muted">
                        Режим: {statusLabel(batch.source_type)}. Ключ: {batch.api_key_masked || '-'}. URL: {batch.api_url || '-'}.
                        {!isTochka ? <>{batch.adv_code ? ` advCode: ${batch.adv_code}.` : ''} productCode: {batch.product_code || 'LP_RKO'}.</> : null}
                    </p>

                    <div className="summary-grid">
                        <div className="card">
                            <strong>{batch.uploaded_total}</strong>
                            <br />
                            <span className="muted">загружено из файла</span>
                        </div>

                        <div className="card">
                            <strong>{batch.unique_total}</strong>
                            <br />
                            <span className="muted">уникальных после дублей</span>
                        </div>

                        <div className="card">
                            <strong>{batch.found_total}</strong>
                            <br />
                            <span className="muted">найдено / строк принято</span>
                        </div>

                        <div className="card">
                            <strong>{batch.ready_total}</strong>
                            <br />
                            <span className="muted">готово к отправке</span>
                        </div>
                    </div>

                    {isContactsLookupBatch ? (
                        <div className="inline-actions">
                            <button
                                type="button"
                                onClick={createQueue}
                                disabled={!batch?.id || queueing || queue.length > 0}
                            >
                                {queueing ? 'Создаем очередь...' : 'Сформировать очередь отправки'}
                            </button>

                            <button
                                type="button"
                                className="action-button export-button"
                                onClick={() => downloadBatchExcel(batch.id)}
                                disabled={actionLoadingId === `excel:${batch.id}`}
                            >
                                {actionLoadingId === `excel:${batch.id}` ? 'Готовим...' : 'Excel'}
                            </button>

                            {Number(batch.error_total || 0) > 0 ? (
                                <button
                                    type="button"
                                    onClick={() => retryBatchErrors(batch.id)}
                                    disabled={actionLoadingId === `retry:${batch.id}`}
                                >
                                    {actionLoadingId === `retry:${batch.id}` ? 'Ставим...' : 'Переотправить ошибки'}
                                </button>
                            ) : null}

                            <button
                                type="button"
                                className="danger"
                                onClick={() => deleteBatch(batch.id)}
                                disabled={actionLoadingId === `delete:${batch.id}`}
                            >
                                {actionLoadingId === `delete:${batch.id}` ? 'Удаляем...' : 'Удалить загрузку'}
                            </button>
                        </div>
                    ) : (
                        <div className="inline-actions">
                            <button
                                type="button"
                                className="action-button export-button"
                                onClick={() => downloadBatchExcel(batch.id)}
                                disabled={actionLoadingId === `excel:${batch.id}`}
                            >
                                {actionLoadingId === `excel:${batch.id}` ? 'Готовим...' : 'Excel'}
                            </button>

                            {Number(batch.error_total || 0) > 0 ? (
                                <button
                                    type="button"
                                    onClick={() => retryBatchErrors(batch.id)}
                                    disabled={actionLoadingId === `retry:${batch.id}`}
                                >
                                    {actionLoadingId === `retry:${batch.id}` ? 'Ставим...' : 'Переотправить ошибки'}
                                </button>
                            ) : null}

                            <button
                                type="button"
                                className="danger"
                                onClick={() => deleteBatch(batch.id)}
                                disabled={actionLoadingId === `delete:${batch.id}`}
                            >
                                {actionLoadingId === `delete:${batch.id}` ? 'Удаляем...' : 'Удалить загрузку'}
                            </button>
                        </div>
                    )}

                    <div className="table-wrap">
                        <table className="table">
                            <thead>
                            <tr>
                                <th>Статус</th>
                                <th>Название из файла</th>
                                <th>Организация</th>
                                <th>ИНН</th>
                                <th>Телефон</th>
                                <th>ФИО</th>
                                <th>Город</th>
                                <th>CityCode</th>
                                <th>{isTochka ? 'Комментарий' : 'advCode'}</th>
                                <th>Причина</th>
                            </tr>
                            </thead>

                            <tbody>
                            {rows.map((row) => {
                                const contact = row.contact_snapshot || {};

                                return (
                                    <tr key={row.id}>
                                        <td>{row.ready ? 'Готов' : row.found ? 'Не готов' : 'Не найден'}</td>
                                        <td>{row.uploaded_name}</td>
                                        <td>{contact.company_name || '-'}</td>
                                        <td>{contact.inn || '-'}</td>
                                        <td>{contact.phone || '-'}</td>
                                        <td>{contact.fio || '-'}</td>
                                        <td>
                                            {contact.city || '-'}
                                            {contact.city_auto_detected ? (
                                                <>
                                                    <br />
                                                    <span className="muted">авто по ИНН</span>
                                                </>
                                            ) : null}
                                        </td>
                                        <td>{contact.city_code || '-'}</td>
                                        <td>{isTochka ? (contact.call_result_comment || '-') : (contact.advCode || batch.adv_code || '-')}</td>
                                        <td>{row.reason || '-'}</td>
                                    </tr>
                                );
                            })}

                            {!rows.length ? (
                                <tr>
                                    <td colSpan="10">Нет строк</td>
                                </tr>
                            ) : null}
                            </tbody>
                        </table>
                    </div>
                </section>
            ) : null}

            {batch ? (
                <section className="card">
                    <h3>Визуальная очередь отправки</h3>

                    <div className="table-wrap">
                        <table className="table">
                            <thead>
                            <tr>
                                <th>ID</th>
                                <th>Статус</th>
                                <th>Организация</th>
                                <th>ИНН</th>
                                <th>Телефон</th>
                                <th>Город</th>
                                <th>Bank request ID</th>
                                <th>Попытки</th>
                                <th>Отправлено</th>
                                <th>Ошибка</th>
                            </tr>
                            </thead>

                            <tbody>
                            {queue.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.id}</td>
                                    <td>{statusLabel(item.status)}</td>
                                    <td>{item.company_name}</td>
                                    <td>{item.inn || '-'}</td>
                                    <td>{item.phone || '-'}</td>
                                    <td>
                                        {item.city || '-'}
                                        <br />
                                        <span className="muted">{item.city_code || '-'}</span>
                                    </td>
                                    <td>{item.bank_request_id || '-'}</td>
                                    <td>{item.attempts}</td>
                                    <td>{formatDate(item.sent_at)}</td>
                                    <td>{item.error_text || '-'}</td>
                                </tr>
                            ))}

                            {!queue.length ? (
                                <tr>
                                    <td colSpan="10">Очередь еще не сформирована</td>
                                </tr>
                            ) : null}
                            </tbody>
                        </table>
                    </div>
                </section>
            ) : null}
        </div>
    );
}
